import { useState, useRef, useCallback, useMemo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTenant } from '@/contexts/TenantProvider'
import { useSectionSettings, useToggleSection, useReorderSections, useSiteContent } from '@/hooks'
import SectionSortable from '@/components/cms/SectionSortable'
import SectionContentEditor from '@/components/admin/SectionContentEditor'
import type { SectionContentEditorHandle } from '@/components/admin/SectionContentEditor'
import { FeatureGate } from '@/components/FeatureGate'
import AdminPageFrame from '@/components/admin/AdminPageFrame'
import {
  Activity,
  ArrowUpRight,
  Clock3,
  ExternalLink,
  Globe,
  Layers,
  Palette,
  RefreshCcw,
  Sparkles,
} from 'lucide-react'
import PublishFlowModal from '@/components/admin/PublishFlowModal'

export default function AdminContent() {
  const { tenant } = useTenant()
  const tenantId = tenant?.id
  const { data: sections = [], isLoading } = useSectionSettings(tenantId)
  const { data: siteData } = useSiteContent(tenantId)
  const toggleMutation = useToggleSection()
  const reorderMutation = useReorderSections()
  const [showPublish, setShowPublish] = useState(false)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [previewSeed, setPreviewSeed] = useState(0)

  // Track editor refs so we can flush unsaved content before publish
  const editorRefs = useRef<Map<string, SectionContentEditorHandle>>(new Map())

  const setEditorRef = useCallback(
    (sectionKey: string, handle: SectionContentEditorHandle | null) => {
      if (handle) {
        editorRefs.current.set(sectionKey, handle)
      } else {
        editorRefs.current.delete(sectionKey)
      }
    },
    []
  )

  /** Flush all dirty editors, then open publish modal */
  const handlePublish = useCallback(() => {
    for (const editor of editorRefs.current.values()) {
      if (editor.isDirty) editor.flush()
    }
    // Small delay to let saves fire before showing modal
    setTimeout(() => setShowPublish(true), 100)
  }, [])

  const handleToggle = (id: string, enabled: boolean) => {
    toggleMutation.mutate({ id, enabled })
  }

  const handleReorder = (updates: Array<{ id: string; display_order: number }>) => {
    reorderMutation.mutate(updates)
  }

  const enabledCount = sections.filter((section) => section.enabled).length
  const draftCount = sections.length - enabledCount
  const topSection = sections[0]
  const lastSyncLabel =
    reorderMutation.isPending || toggleMutation.isPending ? 'Syncing now' : 'Just now'
  const activeTemplate = siteData?.grouped?.site?.active_template
  const slug = tenant?.slug
  const previewUrl = useMemo(() => {
    if (!slug) return ''
    const params = new URLSearchParams({ draft: 'true', preview: String(previewSeed) })
    if (activeTemplate) params.set('template', activeTemplate)
    return `/${slug}?${params.toString()}`
  }, [slug, activeTemplate, previewSeed])

  return (
    <AdminPageFrame
      title="Content Sections"
      icon={<Layers size={14} className="text-origin-blue" />}
      subtitle="Arrange sections, edit content, and publish changes when ready."
      bodyClassName="bg-bg-tertiary p-6"
      actions={
        <>
          <FeatureGate feature="cmsEditor">
            <Link
              to={`/${tenant?.slug}/admin/editor`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-origin-blue bg-surface px-3 py-2 text-xs font-semibold text-origin-blue transition-colors hover:bg-[#cde5ff]"
            >
              <ExternalLink size={11} /> Open Editor
            </Link>
          </FeatureGate>
          <button
            onClick={handlePublish}
            className="inline-flex items-center gap-1.5 rounded-lg border border-origin-blue bg-origin-blue px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#004c83]"
          >
            <Globe size={11} /> Publish Changes
          </button>
        </>
      }
    >
      <div className="space-y-6">
        <section className="grid gap-4 lg:grid-cols-[1.6fr_0.9fr]">
          <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <p className="text-xs font-mono uppercase tracking-[0.28em] text-origin-blue">
                  Content management
                </p>
                <h2 className="mt-2 text-3xl font-semibold tracking-tight text-text-primary">
                  Curate the live site structure
                </h2>
                <p className="mt-3 text-sm leading-6 text-text-secondary">
                  Reorder sections, open the inline editor, and keep the booking journey aligned
                  with the current campaign.
                </p>
              </div>
              <div className="rounded-xl border border-[#cde5ff] bg-[#f2f8ff] px-4 py-3">
                <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-text-secondary">
                  Last sync
                </p>
                <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-text-primary">
                  <Clock3 size={14} className="text-origin-blue" />
                  {lastSyncLabel}
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <MetricCard
                label="Sections"
                value={String(sections.length)}
                accent="border-l-origin-blue"
                icon={<Layers size={14} className="text-origin-blue" />}
              />
              <MetricCard
                label="Live"
                value={String(enabledCount)}
                accent="border-l-emerald-500"
                icon={<Activity size={14} className="text-emerald-600" />}
              />
              <MetricCard
                label="Drafts"
                value={String(draftCount)}
                accent="border-l-amber-500"
                icon={<Sparkles size={14} className="text-status-warning-text" />}
              />
            </div>
          </div>

          <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.24em] text-text-secondary">
                  Live draft preview
                </p>
                <h3 className="mt-2 text-lg font-semibold text-text-primary">Tenant site (live)</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPreviewSeed((seed) => seed + 1)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-text-secondary transition-colors hover:border-origin-blue hover:text-origin-blue"
                >
                  <RefreshCcw size={11} />
                  Refresh
                </button>
                <span className="rounded-full bg-[#cde5ff] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-origin-blue">
                  Draft
                </span>
              </div>
            </div>

            <div className="mt-5 overflow-hidden rounded-xl border border-border bg-bg-tertiary">
              {previewUrl ? (
                <iframe
                  title="Tenant live draft preview"
                  src={previewUrl}
                  className="aspect-[4/3] w-full bg-surface"
                />
              ) : (
                <div className="aspect-[4/3] flex items-center justify-center text-xs text-text-secondary">
                  Preview unavailable until tenant slug resolves.
                </div>
              )}
            </div>

            {topSection ? (
              <div className="mt-5 rounded-xl border border-border bg-bg-tertiary p-4">
                <p className="text-xs font-mono uppercase tracking-[0.22em] text-text-secondary">
                  Next in queue
                </p>
                <p className="mt-2 text-sm font-semibold text-text-primary">
                  {topSection.section_key}
                </p>
                <p className="mt-1 text-xs text-text-secondary">
                  Drag this section to reorder, then confirm in live preview.
                </p>
              </div>
            ) : null}
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.7fr_0.95fr]">
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="text-xl font-semibold text-text-primary">Site sections</h3>
                <p className="mt-1 text-sm text-text-secondary">
                  Click to edit, drag to reorder, and toggle visibility.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border border-[#cde5ff] bg-[#f2f8ff] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-origin-blue">
                  All {sections.length}
                </span>
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-700">
                  Live {enabledCount}
                </span>
                <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                  Drafts {draftCount}
                </span>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-surface p-2 shadow-sm">
              {isLoading ? (
                <div className="space-y-2 p-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-20 animate-pulse rounded-xl bg-surface-hover" />
                  ))}
                </div>
              ) : sections.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                  <Layers size={28} className="text-[#cde5ff]" />
                  <span className="text-xs font-mono text-text-secondary">
                    No sections configured yet
                  </span>
                </div>
              ) : (
                <SectionSortable
                  sections={sections}
                  onReorder={handleReorder}
                  onToggle={handleToggle}
                  isReordering={reorderMutation.isPending}
                  isToggling={toggleMutation.isPending}
                  expandedKey={expandedKey}
                  onSectionClick={(key) => setExpandedKey(key || null)}
                  renderExpanded={(section) => (
                    <SectionContentEditor
                      ref={(handle) => setEditorRef(section.section_key, handle)}
                      sectionKey={section.section_key}
                    />
                  )}
                />
              )}
            </div>

            {sections.length > 0 && (
              <p className="text-center text-[10px] font-mono text-text-secondary">
                Changes auto-save when you click away. Publish when the site is ready.
              </p>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.22em] text-text-secondary">
                    Content health
                  </p>
                  <h3 className="mt-2 text-lg font-semibold text-text-primary">
                    Editorial readiness
                  </h3>
                </div>
                <Palette size={16} className="text-origin-blue" />
              </div>

              <div className="mt-5 space-y-4">
                <HealthMeter label="SEO integrity" value={94} tone="emerald" />
                <HealthMeter label="Asset optimization" value={82} tone="blue" />
                <HealthMeter label="Publishing queue" value={68} tone="amber" />
              </div>
            </div>

            <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.22em] text-text-secondary">
                    Quick actions
                  </p>
                  <h3 className="mt-2 text-lg font-semibold text-text-primary">
                    Operational controls
                  </h3>
                </div>
                <ArrowUpRight size={16} className="text-origin-blue" />
              </div>

              <div className="mt-5 space-y-3">
                <div className="rounded-xl border border-[#cde5ff] bg-[#f7fbff] p-4">
                  <p className="text-sm font-semibold text-text-primary">Open CMS editor</p>
                  <p className="mt-1 text-xs text-text-secondary">
                    Launch the richer editor for page-level content updates.
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-bg-tertiary p-4">
                  <p className="text-sm font-semibold text-text-primary">Publish changes</p>
                  <p className="mt-1 text-xs text-text-secondary">
                    Push pending updates live after all editors are flushed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <PublishFlowModal open={showPublish} onClose={() => setShowPublish(false)} />
    </AdminPageFrame>
  )
}

function MetricCard({
  label,
  value,
  accent,
  icon,
}: {
  label: string
  value: string
  accent: string
  icon: ReactNode
}) {
  return (
    <div
      className={`rounded-xl border border-border border-l-4 ${accent} bg-surface p-4 shadow-sm`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono uppercase tracking-[0.22em] text-text-secondary">
          {label}
        </span>
        {icon}
      </div>
      <p className="mt-3 text-2xl font-semibold tracking-tight text-text-primary">{value}</p>
    </div>
  )
}

function HealthMeter({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'emerald' | 'blue' | 'amber'
}) {
  const toneClass =
    tone === 'emerald' ? 'bg-emerald-500' : tone === 'blue' ? 'bg-origin-blue' : 'bg-amber-500'
  const trackClass =
    tone === 'emerald' ? 'bg-emerald-100' : tone === 'blue' ? 'bg-[#cde5ff]' : 'bg-amber-100'

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-text-primary">{label}</span>
        <span className="font-mono text-text-secondary">{value}%</span>
      </div>
      <div className={`h-2 overflow-hidden rounded-full ${trackClass}`}>
        <div className={`h-full rounded-full ${toneClass}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  )
}
